/**
 * MsGraphAuthService - Centralized authentication service for MS Graph
 *
 * This service consolidates all MSAL and MS Graph authentication concerns into
 * a single, well-tested class. It eliminates code duplication, implements proper
 * token caching with deduplication, and provides a clean interface for all
 * authentication-related operations.
 *
 * Key features:
 * - Singleton pattern for consistent state
 * - Token acquisition with request deduplication
 * - Smart token refresh (only when needed)
 * - Separate handling of tenant accounts vs calendar accounts
 * - Centralized MS Graph API calls
 * - Proper error handling and recovery
 */

import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  SilentRequest,
} from "@azure/msal-browser";
import settings from "../appSettings";
import { loginRequest } from "../authConfig";
import {
  ApiTokenResult,
  Calendar,
  CalendarEvent,
  GraphPagedResponse,
  GraphTokenResult,
} from "../@types/graph";

// Constants
const LAST_LOGIN_REDIRECT_KEY = "lastLoginRedirect";
const REDIRECT_THROTTLE_MS = 60 * 1000; // 1 minute between redirects
const DEFAULT_GRAPH_SCOPES = ["https://graph.microsoft.com/Calendars.Read"];
const GRAPH_API_BASE = "https://graph.microsoft.com/v1.0";

/**
 * Thrown when a token-redirect is already in progress and was throttled.
 * Callers should NOT treat this as a genuine auth failure — the browser
 * will navigate to the identity provider momentarily.
 */
export class RedirectInProgressError extends Error {
  constructor() {
    super("Authentication redirect is in progress");
    this.name = "RedirectInProgressError";
  }
}

/**
 * Thrown when an infinite redirect loop is detected (e.g. AAD redirects back
 * without proper tokens, resulting in immediate failure again).
 */
export class AuthLoopError extends Error {
  constructor() {
    super("Authentication loop detected");
    this.name = "AuthLoopError";
  }
}

// Default date range for calendar events: 14 days back, 42 days forward
const DEFAULT_PAST_DAYS = 14;
const DEFAULT_FUTURE_DAYS = 42;

/**
 * Options for acquiring tokens
 */
export interface TokenAcquisitionOptions {
  scopes?: string[];
  account?: AccountInfo;
  authority?: string;
  forceRefresh?: boolean;
}

/**
 * MsGraphAuthService - Singleton authentication service
 */
export class MsGraphAuthService {
  private static instance: MsGraphAuthService;
  private msalInstance: IPublicClientApplication | null = null;
  private isRedirecting = false; // Tracks if a redirect was initiated in this session

  // Map to track in-progress token acquisitions for deduplication
  private tokenAcquisitionPromises: Map<string, Promise<AuthenticationResult>> =
    new Map();

  private constructor() {
    // Private constructor for singleton
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): MsGraphAuthService {
    if (!MsGraphAuthService.instance) {
      MsGraphAuthService.instance = new MsGraphAuthService();
    }
    return MsGraphAuthService.instance;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Initialization
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Initialize the service with an MSAL instance
   * Must be called once during app startup before any other methods
   */
  public initialize(msal: IPublicClientApplication): void {
    this.msalInstance = msal;
  }

  /**
   * Check if the service has been initialized
   */
  public isInitialized(): boolean {
    return this.msalInstance !== null;
  }

  /**
   * Get the underlying MSAL instance
   * @throws Error if not initialized
   */
  public getMsalInstance(): IPublicClientApplication {
    if (!this.msalInstance) {
      throw new Error(
        "MsGraphAuthService not initialized. Call initialize() first.",
      );
    }
    return this.msalInstance;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Account Management
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Get all accounts in the MSAL cache
   */
  public getAllAccounts(): AccountInfo[] {
    return this.getMsalInstance().getAllAccounts();
  }

  /**
   * Get the account from the app's configured tenant
   * This is the primary account for API authentication
   */
  public getTenantAccount(): AccountInfo | null {
    const accounts = this.getAllAccounts();
    return accounts.find((a) => a.tenantId === settings.tenantId) ?? null;
  }

  /**
   * Get a calendar-connected account by username
   * Used for fetching calendar events from personal/external accounts
   */
  public getCalendarAccount(username: string): AccountInfo | null {
    const normalizedUsername = username.toLowerCase();
    const accounts = this.getAllAccounts();
    return (
      accounts.find((a) => a.username.toLowerCase() === normalizedUsername) ??
      null
    );
  }

  /**
   * Get the currently active account
   */
  public getActiveAccount(): AccountInfo | null {
    return this.getMsalInstance().getActiveAccount();
  }

  /**
   * Set the active account
   */
  public setActiveAccount(account: AccountInfo | null): void {
    this.getMsalInstance().setActiveAccount(account ?? undefined);
    if (account) {
      console.info(
        "[MsGraphAuthService] Active account set:",
        account.username,
      );
    }
  }

  /**
   * Remove a specific account from the MSAL cache
   * Used when disconnecting calendar accounts
   */
  public async removeAccount(account: AccountInfo): Promise<void> {
    // Don't remove the app's tenant account
    if (account.tenantId === settings.tenantId) {
      console.warn(
        "[MsGraphAuthService] Cannot remove tenant account from cache",
      );
      return;
    }

    try {
      await this.getMsalInstance().clearCache({ account });
      console.info(
        "[MsGraphAuthService] Removed account from cache:",
        account.username,
      );
    } catch (error) {
      console.warn(
        "[MsGraphAuthService] Failed to remove account from cache:",
        error,
      );
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Token Acquisition
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Generate a cache key for token deduplication
   */
  private getTokenCacheKey(request: SilentRequest): string {
    const scopes = [...request.scopes].sort().join(",");
    const account = request.account?.username ?? "none";
    const authority = request.authority ?? "default";
    return `${scopes}|${account}|${authority}`;
  }

  /**
   * Acquire token with request deduplication
   * Prevents multiple simultaneous requests for the same token
   */
  private async acquireTokenWithDeduplication(
    request: SilentRequest,
  ): Promise<AuthenticationResult> {
    const cacheKey = this.getTokenCacheKey(request);

    // Return existing promise if acquisition is in progress
    const existingPromise = this.tokenAcquisitionPromises.get(cacheKey);
    if (existingPromise) {
      console.debug(
        "[MsGraphAuthService] Returning deduplicated token request",
      );
      return existingPromise;
    }

    // Start new acquisition
    const promise = this.getMsalInstance().acquireTokenSilent(request);
    this.tokenAcquisitionPromises.set(cacheKey, promise);

    try {
      return await promise;
    } finally {
      this.tokenAcquisitionPromises.delete(cacheKey);
    }
  }

  /**
   * Check if we should throttle redirects
   */
  private shouldThrottleRedirect(): boolean {
    const lastRedirect = localStorage.getItem(LAST_LOGIN_REDIRECT_KEY);
    if (!lastRedirect) return false;

    const lastTime = parseInt(lastRedirect, 10);
    return Date.now() - lastTime < REDIRECT_THROTTLE_MS;
  }

  /**
   * Record a redirect timestamp for throttling
   */
  private recordRedirect(): void {
    localStorage.setItem(LAST_LOGIN_REDIRECT_KEY, Date.now().toString());
  }

  /**
   * Get token for backend API calls
   * Uses the tenant account and configured scopes
   */
  public async getApiToken(): Promise<ApiTokenResult | null> {
    const account = this.getTenantAccount() ?? this.getActiveAccount();

    const request: SilentRequest = {
      scopes: loginRequest.scopes,
      account: account ?? undefined,
      authority: `https://login.microsoftonline.com/${settings.tenantId}`,
    };

    try {
      const result = await this.acquireTokenWithDeduplication(request);
      return {
        accessToken: result.accessToken,
        idToken: result.idToken,
        expiresOn: result.expiresOn ?? undefined,
      };
    } catch (error) {
      return this.handleTokenError(error, request);
    }
  }

  /**
   * Get token for MS Graph API calls
   * Can use different scopes and accounts (e.g., for calendar access)
   */
  public async getGraphToken(
    options: TokenAcquisitionOptions = {},
  ): Promise<GraphTokenResult> {
    const {
      scopes = DEFAULT_GRAPH_SCOPES,
      account,
      authority = "https://login.microsoftonline.com/common",
      forceRefresh = false,
    } = options;

    const resolvedAccount =
      account ?? this.getTenantAccount() ?? this.getActiveAccount();

    const request: SilentRequest = {
      scopes,
      account: resolvedAccount ?? undefined,
      authority,
      forceRefresh,
    };

    try {
      const result = await this.acquireTokenWithDeduplication(request);
      return {
        accessToken: result.accessToken,
        expiresOn: result.expiresOn,
        account: result.account ?? undefined,
      };
    } catch (error) {
      if (
        error instanceof InteractionRequiredAuthError ||
        error instanceof BrowserAuthError
      ) {
        // For Graph tokens (calendar), we might need popup/redirect
        throw error;
      }
      throw error;
    }
  }

  /**
   * Handle token acquisition errors
   * When a redirect is triggered, returns a promise that never resolves
   * (the browser is navigating away, so the caller should not continue).
   * When the redirect is throttled, throws a recognizable error so callers
   * can distinguish "redirect in progress" from "genuinely failed".
   */
  private async handleTokenError(
    error: unknown,
    request: SilentRequest,
  ): Promise<null> {
    if (
      error instanceof InteractionRequiredAuthError ||
      error instanceof BrowserAuthError
    ) {
      console.info(
        `[MsGraphAuthService] Silent token acquisition failed (${error.name}: ${error.message}). Triggering redirect...`,
      );

      if (this.isRedirecting) {
        // A concurrent request in THIS browser tab already started the redirect.
        // Throw an error so the caller can pause execution.
        throw new RedirectInProgressError();
      }

      if (!this.shouldThrottleRedirect()) {
        this.isRedirecting = true;
        this.recordRedirect();
        
        // Start the redirect — the browser will navigate away.
        this.getMsalInstance().acquireTokenRedirect(request).catch((err) => {
          console.error("[MsGraphAuthService] acquireTokenRedirect failed:", err);
          this.isRedirecting = false; // Reset if it failed synchronously
        });
        
        // Return a promise that never resolves so callers don't
        // continue executing as if the request failed.
        return new Promise<null>(() => {
          // intentionally never resolves — browser is redirecting
        });
      } else {
        console.error("[MsGraphAuthService] Auth loop detected (redirect throttled)");
        // Throw a specific error so callers can show a permanent failure state
        throw new AuthLoopError();
      }
    } else {
      console.error("[MsGraphAuthService] Token acquisition failed:", error);
    }

    return null;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Login / Logout
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Determine if /common authority should be used based on email domain
   */
  private shouldUseCommonAuthority(email: string): boolean {
    if (settings.loginAuthorityMode === "common") return true;
    if (settings.loginAuthorityMode !== "auto") return false;

    const normalizedEmail = email.toLowerCase();
    return settings.commonLoginDomains.some((domain) => {
      const normalizedDomain = domain.trim().toLowerCase();
      return (
        normalizedEmail.endsWith(`@${normalizedDomain}`) ||
        normalizedEmail.endsWith(normalizedDomain)
      );
    });
  }

  /**
   * Initiate login flow
   */
  public async login(email: string): Promise<void> {
    const normalizedEmail = email.trim().toLowerCase();
    const authority = this.shouldUseCommonAuthority(normalizedEmail)
      ? "https://login.microsoftonline.com/common"
      : `https://login.microsoftonline.com/${settings.tenantId}`;

    try {
      await this.getMsalInstance().loginRedirect({
        ...loginRequest,
        loginHint: normalizedEmail,
        authority,
      });
    } catch (error) {
      console.error("[MsGraphAuthService] Login failed:", error);
      throw error;
    }
  }

  /**
   * Initiate logout flow
   */
  public async logout(): Promise<void> {
    const account = this.getActiveAccount();
    try {
      await this.getMsalInstance().logoutRedirect({
        account: account ?? undefined,
      });
    } catch (error) {
      console.error("[MsGraphAuthService] Logout failed:", error);
      throw error;
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // MS Graph API Operations
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Make an authenticated request to MS Graph API
   */
  private async graphRequest<T>(
    endpoint: string,
    accessToken: string,
  ): Promise<T> {
    const url = endpoint.startsWith("http")
      ? endpoint
      : `${GRAPH_API_BASE}${endpoint}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const body = await response.text();
      const wwwAuth = response.headers.get("WWW-Authenticate") || "";
      throw new Error(
        `Graph API request failed: ${response.status} ${wwwAuth} ${body}`,
      );
    }

    return response.json();
  }

  /**
   * Fetch the user's primary calendar
   */
  public async fetchCalendar(accessToken: string): Promise<Calendar> {
    return this.graphRequest<Calendar>("/me/calendar", accessToken);
  }

  /**
   * Fetch calendar events with pagination support
   */
  public async fetchCalendarEvents(
    account: AccountInfo,
    startDateTime?: Date,
    endDateTime?: Date,
  ): Promise<CalendarEvent[]> {
    // Acquire token for the calendar account (no forceRefresh unless needed)
    const tokenResult = await this.getGraphToken({
      scopes: DEFAULT_GRAPH_SCOPES,
      account,
      authority: "https://login.microsoftonline.com/common",
      forceRefresh: false, // Let MSAL handle caching
    });

    const start =
      startDateTime ??
      new Date(Date.now() - DEFAULT_PAST_DAYS * 24 * 60 * 60 * 1000);
    const end =
      endDateTime ??
      new Date(Date.now() + DEFAULT_FUTURE_DAYS * 24 * 60 * 60 * 1000);

    const allEvents: CalendarEvent[] = [];
    let nextUrl: string | undefined =
      `/me/calendarview?$top=1000&startdatetime=${encodeURIComponent(start.toISOString())}&enddatetime=${encodeURIComponent(end.toISOString())}`;

    while (nextUrl) {
      const response = await this.graphRequest<
        GraphPagedResponse<CalendarEvent>
      >(nextUrl, tokenResult.accessToken);

      allEvents.push(...(response.value ?? []));
      nextUrl = response["@odata.nextLink"];
    }

    return allEvents;
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Error Recovery
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Handle 401 errors from API calls
   * Triggers re-authentication if needed
   */
  public async handle401Error(): Promise<void> {
    if (this.shouldThrottleRedirect()) {
      console.warn(
        "[MsGraphAuthService] 401 redirect throttled to prevent loop",
      );
      return;
    }

    this.recordRedirect();

    const account = this.getTenantAccount() ?? this.getActiveAccount();
    await this.getMsalInstance().acquireTokenRedirect({
      scopes: loginRequest.scopes,
      account: account ?? undefined,
      authority: `https://login.microsoftonline.com/${settings.tenantId}`,
    });
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Advanced Token Acquisition
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Acquire token with interactive fallback (popup)
   * Use this when silent acquisition fails and you need to prompt for consent
   */
  public async getGraphTokenWithPopupFallback(
    options: TokenAcquisitionOptions & { promptConsent?: boolean },
  ): Promise<GraphTokenResult> {
    const {
      scopes = DEFAULT_GRAPH_SCOPES,
      account,
      authority = "https://login.microsoftonline.com/common",
      forceRefresh = false,
      promptConsent = false,
    } = options;

    const resolvedAccount = account ?? undefined;

    const request: SilentRequest = {
      scopes,
      account: resolvedAccount,
      authority,
      forceRefresh,
    };

    try {
      const result = await this.acquireTokenWithDeduplication(request);
      return {
        accessToken: result.accessToken,
        expiresOn: result.expiresOn,
        account: result.account ?? undefined,
      };
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Fall back to popup
        const popupResult = await this.getMsalInstance().acquireTokenPopup({
          ...request,
          prompt: promptConsent ? "consent" : undefined,
        });
        return {
          accessToken: popupResult.accessToken,
          expiresOn: popupResult.expiresOn,
          account: popupResult.account ?? undefined,
        };
      }
      throw error;
    }
  }

  /**
   * Handle redirect promise after a redirect-based auth flow
   * Should be called on page load to process redirect results
   */
  public async handleRedirectPromise(): Promise<AuthenticationResult | null> {
    return this.getMsalInstance().handleRedirectPromise();
  }

  /**
   * Get all accounts in MSAL cache as a Set of normalized usernames
   */
  public getAccountUsernamesInCache(): Set<string> {
    try {
      return new Set(
        this.getAllAccounts()
          .map((account) => account.username.trim().toLowerCase())
          .filter((username) => !!username),
      );
    } catch {
      return new Set();
    }
  }
}

// Export singleton instance for convenience
export const msGraphAuthService = MsGraphAuthService.getInstance();
